import L from "leaflet";
import * as store from "./store";
import { onRemoteChange, onSyncState, setActiveGarden, syncSoon } from "./sync";
import { createMap, startGps, type MapRotation } from "./map";
import { $, h, norm, plural, spColor, toast } from "./util";
import { I } from "./icons";
import { STAV_LABEL, type Garden, type Photo, type Plant, type Stav } from "./types";
import type { Me } from "./auth";
import { openForm } from "./plantform";
import { openCard, openList, openLayers, openMenu } from "./sheets";
import { openParcels } from "./parcels";
import { isDemo } from "./demo";

export interface PlantFilter {
  q: string;
  druh: string;
  stav: Stav | "";
}

export interface NearestInfo {
  plant: Plant;
  dist: number;
  distText: string;
}

export interface Ctx {
  garden: Garden; me: Me; root: HTMLElement;
  map: L.Map; kn: L.TileLayer.WMS; rot: MapRotation;
  plantLayer: L.LayerGroup; parcelLayer: L.FeatureGroup; distLayer: L.LayerGroup; subgardenLayer: L.LayerGroup;
  plants: Map<string, Plant>; photos: Map<string, Photo[]>; pending: Set<string>;
  subgardens: Garden[]; parentGarden: Garden | null;
  selected: string | null; showNumbers: boolean; showNearestDist: boolean;
  filter: PlantFilter;
  gps: ReturnType<typeof startGps>;
  nav: { back: () => void; logout: () => void; openGarden: (g: Garden, editParcels?: boolean) => void };
  showSheet(id: string | null): void;
  reload(): Promise<void>;
  select(id: string | null): void;
  startPlacing(
    kind: "new" | "move",
    start: L.LatLng | null,
    cb: (ll: L.LatLng) => void,
    onCancel?: () => void,
    excludePlantId?: string | null,
  ): void;
  drawParcels(): void;
  drawSubgardens(): Promise<void>;
  fitGarden(): void;
  updateGardenPhoto(g: Garden): void;
  setGardenName(name: string): void;
  setShowNearestDist(on: boolean): void;
  nearestTo(lat: number, lng: number, excludeId?: string | null): NearestInfo | null;
  matchesFilter(t: Plant): boolean;
  hasActiveFilter(): boolean;
  applyFilter(): void;
  label(t: Plant): string;
  nextNumber(): number;
}

const SHEETS = [
  "placeSheet", "formSheet", "cardSheet", "listSheet", "layerSheet",
  "menuSheet", "parcelsSheet", "offlineSheet", "shareSheet", "photoSheet", "subgardensSheet",
];

const SKELETON = `
<div id="map" aria-label="Mapa zahrady"></div>
<div id="cross" hidden><i></i></div>
<header class="topbar">
  <button class="brand" id="brand" aria-label="Zpět na seznam zahrad"><b id="gName"></b><small><span class="dot off" id="syncDot"></span><span id="syncText">Načítám…</span></small></button>
  <button class="iconbtn" id="btnList" aria-label="Seznam a filtr rostlin">${I.list}<span id="count">0</span></button>
  <button class="iconbtn" id="btnMenu" aria-label="Nabídka">${I.menu}</button>
</header>
<div class="top-pills" id="topPills">
  <button class="top-pill parent-pill" id="btnParentGarden" hidden></button>
  <div class="top-pill filter-pill" id="filterBar" hidden>
    <span id="filterBarText"></span>
    <button type="button" id="filterBarClear" aria-label="Zrušit filtr" title="Zobrazit všechny rostliny">× Zrušit</button>
  </div>
  <div class="banner" id="banner" hidden><div id="bannerText"></div></div>
</div>
<div class="fab-row" id="fabRow">
  <button class="fab" id="btnAdd">${I.plus}Přidat rostlinu</button>
  <div class="sidebtns">
    <button class="iconbtn compass" id="btnCompass" aria-label="Kompas – sever nahoře" title="Klepnutím srovnáš sever nahoru, tažením otočíš mapu">${I.compass}</button>
    <button class="iconbtn" id="btnLocate" aria-label="Moje poloha">${I.locate}</button>
    <button class="iconbtn" id="btnLayers" aria-label="Vrstvy mapy">${I.layers}</button>
    <button class="iconbtn" id="btnFit" aria-label="Zobrazit celou zahradu">${I.fit}</button>
  </div>
</div>
<section class="sheet" id="placeSheet" hidden>
  <div class="sheet-body" style="padding-top:14px">
    <p class="hint" id="placeHint"><b>Posuň mapu</b> tak, aby byl křížek přesně na místě výsadby. Klepnutím do mapy tam křížek skočí, tlačítkem GPS ho přesuneš na svou polohu.</p>
    <div class="row-between" id="placeDistRow">
      <button type="button" class="chip" id="placeDistToggle" aria-pressed="false">${I.ruler}<span>Vzdálenost od nejbližší</span></button>
      <small class="hint" id="placeDistText"></small>
    </div>
    <div class="btns"><button class="btn ghost" id="placeCancel">Zrušit</button><button class="btn ghost" id="placeGps" aria-label="Na moji polohu">${I.locate}</button><button class="btn primary" id="placeOk">Umístit sem</button></div>
  </div>
</section>
<section class="sheet" id="formSheet" hidden></section>
<section class="sheet" id="cardSheet" hidden></section>
<section class="sheet" id="listSheet" hidden></section>
<section class="sheet" id="layerSheet" hidden></section>
<section class="sheet" id="menuSheet" hidden></section>
<section class="sheet" id="parcelsSheet" hidden></section>
<section class="sheet" id="offlineSheet" hidden></section>
<section class="sheet" id="shareSheet" hidden></section>
<section class="sheet" id="photoSheet" hidden></section>
<section class="sheet" id="subgardensSheet" hidden></section>
`;

export async function renderGarden(root: HTMLElement, garden: Garden, me: Me, nav: Ctx["nav"], editParcels = false) {
  root.innerHTML = SKELETON;
  const readOnly = garden.role === "read";
  const isOwner = !garden.role || garden.role === "owner";
  const isPhoto = garden.mode === "photo";

  function renderHeaderTitle(name: string) {
    const gNameEl = $("#gName", root);
    gNameEl.textContent = name;
    if (isDemo()) gNameEl.append(" ", h("span", { class: "demo-chip" }, "ukázka"));
    else if (readOnly) gNameEl.append(" ", h("span", { class: "demo-chip" }, "jen čtení"));
  }
  renderHeaderTitle(garden.name);
  if (readOnly) $("#btnAdd", root).hidden = true;

  if (isPhoto) {
    $("#btnLocate", root).hidden = true;
    $("#placeGps", root).hidden = true;
    $("#placeHint", root).innerHTML = "<b>Posuň fotku</b> tak, aby byl křížek přesně na místě rostliny. Klepnutím do fotky tam křížek skočí.";
  }

  const { map, kn, rot, setPhotoOverlay, getPhotoBounds } = createMap(
    $("#map", root),
    isPhoto ? { url: gardenPhotoSrc(garden), width: garden.photo_width, height: garden.photo_height } : undefined,
  );
  const parcelLayer = L.featureGroup().addTo(map);
  const subgardenLayer = L.layerGroup().addTo(map);
  const distLayer = L.layerGroup().addTo(map);
  const plantLayer = L.layerGroup().addTo(map);
  const markers = new Map<string, L.Marker>();
  let placing: { cb: (ll: L.LatLng) => void; onCancel?: () => void; excludeId?: string | null } | null = null;
  let userMoved = false;

  // Restore saved rotation angle for this garden (if any)
  try {
    const savedRot = Number(localStorage.getItem("zahrada:rot:" + garden.id));
    if (Number.isFinite(savedRot) && Math.abs(savedRot) > 0.1) {
      rot.setBearing(savedRot);
    }
  } catch {}

  // Cache photo blob in IndexedDB for offline use when opened on a new device
  if (isPhoto && !garden.photo_blob && !isDemo()) {
    fetch(gardenPhotoSrc(garden), { credentials: "same-origin" })
      .then((r) => (r.ok ? r.blob() : null))
      .then(async (blob) => {
        if (!blob) return;
        ctx.garden = { ...ctx.garden, photo_blob: blob };
        await store.putGarden(ctx.garden);
      })
      .catch(() => {});
  }

  const ctx: Ctx = {
    garden, me, root, map, kn, rot, plantLayer, parcelLayer, distLayer, subgardenLayer,
    plants: new Map(), photos: new Map(), pending: new Set(),
    subgardens: [], parentGarden: null,
    selected: null,
    showNumbers: (() => { try { return localStorage.getItem("zahrada:nums") !== "0"; } catch { return true; } })(),
    showNearestDist: (() => { try { return localStorage.getItem("zahrada:dist") === "1"; } catch { return false; } })(),
    filter: { q: "", druh: "", stav: "" },
    gps: isPhoto
      ? { last: () => null, restart: () => {}, stop: () => {} }
      : startGps(map, (f) => {
          if (!userMoved && !garden.parcels.length) { map.setView([f.lat, f.lng], 18); userMoved = true; }
        }),
    nav,
    showSheet(id) {
      for (const s of SHEETS) $("#" + s, root).hidden = s !== id;
      $("#fabRow", root).hidden = !!id;
      updateBanner();
    },
    async reload() {
      ctx.plants = new Map((await store.plantsOf(garden.id)).map((t) => [t.id, t]));
      const ph = await store.photosOfGarden(garden.id);
      ctx.photos = new Map();
      for (const p of ph) { const a = ctx.photos.get(p.plant_id) ?? []; a.push(p); ctx.photos.set(p.plant_id, a); }
      for (const a of ctx.photos.values()) a.sort((x, y) => x.created_at.localeCompare(y.created_at));
      ctx.pending = new Set((await store.outbox()).map((o) => o.id));
      await drawSubgardens();
      renderMarkers();
      updateFilterBar();
      updateBanner();
    },
    select(id) {
      const prev = ctx.selected; ctx.selected = id;
      for (const k of [prev, id]) if (k) { const t = ctx.plants.get(k); const m = markers.get(k); if (t && m) m.setIcon(iconFor(t)); }
      renderDistOverlay();
    },
    startPlacing(kind, start, cb, onCancel, excludePlantId = null) {
      if (readOnly) return;
      placing = { cb, onCancel, excludeId: excludePlantId };
      rot.setLocked(true);
      ctx.showSheet("placeSheet");
      updatePlaceDistUi();
      requestAnimationFrame(() => {
        const barH = $("#placeSheet", root).offsetHeight, hgt = root.clientHeight || window.innerHeight;
        const cross = $("#cross", root);
        cross.style.top = Math.round((hgt - barH) / 2 + 28) + "px";
        cross.hidden = false;
        const targetZ = isPhoto
          ? Math.min(6, Math.max(map.getZoom(), map.getBoundsZoom(getPhotoBounds()) + 0.75))
          : Math.max(map.getZoom(), 20);
        const minZ = isPhoto ? map.getBoundsZoom(getPhotoBounds()) + 0.5 : 19.5;
        if (start) panToCross(start, targetZ);
        else if (map.getZoom() < minZ) map.setZoomAround(crossPoint(), targetZ);
        renderDistOverlay();
      });
      void kind;
    },
    drawParcels,
    drawSubgardens,
    fitGarden,
    updateGardenPhoto(g: Garden) {
      ctx.garden = g;
      setPhotoOverlay(gardenPhotoSrc(g), g.photo_width, g.photo_height);
      fitGarden();
    },
    setGardenName(name: string) {
      ctx.garden = { ...ctx.garden, name };
      renderHeaderTitle(name);
    },
    setShowNearestDist(on: boolean) {
      ctx.showNearestDist = on;
      try { localStorage.setItem("zahrada:dist", on ? "1" : "0"); } catch {}
      updatePlaceDistUi();
      renderDistOverlay();
    },
    nearestTo(lat: number, lng: number, excludeId: string | null = null): NearestInfo | null {
      let bestPlant: Plant | null = null;
      let bestDist = Infinity;
      for (const t of ctx.plants.values()) {
        if (excludeId && t.id === excludeId) continue;
        const d = isPhoto
          ? (Math.hypot(lat - t.lat, lng - t.lng) / 80) * 100
          : L.latLng(lat, lng).distanceTo([t.lat, t.lng]);
        if (d < bestDist) {
          bestDist = d;
          bestPlant = t;
        }
      }
      if (!bestPlant || !Number.isFinite(bestDist)) return null;
      const distText = isPhoto
        ? bestDist.toFixed(1).replace(".", ",") + " % šířky"
        : bestDist < 100
          ? bestDist.toFixed(1).replace(".", ",") + " m"
          : Math.round(bestDist) + " m";
      return { plant: bestPlant, dist: bestDist, distText };
    },
    matchesFilter(t: Plant) {
      if (ctx.filter.druh && (t.druh || "Neurčeno") !== ctx.filter.druh) return false;
      if (ctx.filter.stav && t.stav !== ctx.filter.stav) return false;
      const needle = norm(ctx.filter.q).trim();
      if (needle && !norm([t.cislo, t.druh, t.odruda, t.podnoz, t.poznamka, t.rok].join(" ")).includes(needle)) return false;
      return true;
    },
    hasActiveFilter() {
      return Boolean(ctx.filter.druh || ctx.filter.stav || ctx.filter.q.trim());
    },
    applyFilter() {
      renderMarkers();
      updateFilterBar();
      updateBanner();
    },
    label: (t) => [t.druh, t.odruda].filter(Boolean).join(" – ") || "Neurčená rostlina",
    nextNumber() { let m = 0; for (const t of ctx.plants.values()) m = Math.max(m, t.cislo ?? 0); return m + 1; },
  };

  function iconFor(t: Plant) {
    const cls = ["tm"];
    if (ctx.pending.has(t.id)) cls.push("pending");
    if (ctx.selected === t.id) cls.push("sel");
    if (t.stav === "uschly") cls.push("dead");
    if (t.stav === "planovany") cls.push("planned");
    const num = ctx.showNumbers && t.cislo != null ? String(t.cislo) : "";
    return L.divIcon({ className: "", html: "<div class=\"" + cls.join(" ") + "\" style=\"--c:" + spColor(t.druh) + "\">" + num + "</div>", iconSize: [28, 28], iconAnchor: [14, 14] });
  }

  function renderMarkers() {
    for (const [id, m] of markers) {
      const t = ctx.plants.get(id);
      if (!t || !ctx.matchesFilter(t)) {
        plantLayer.removeLayer(m);
        markers.delete(id);
      }
    }
    for (const [id, t] of ctx.plants) {
      if (!ctx.matchesFilter(t)) continue;
      let m = markers.get(id);
      if (!m) {
        m = L.marker([t.lat, t.lng], { icon: iconFor(t), title: ctx.label(t), keyboard: true });
        m.on("click", () => { if (!placing) openCard(ctx, id); });
        m.addTo(plantLayer); markers.set(id, m);
      } else { m.setLatLng([t.lat, t.lng]); m.setIcon(iconFor(t)); }
    }
    renderDistOverlay();
  }
  (ctx as any).renderMarkers = renderMarkers;

  function updateFilterBar() {
    const active = ctx.hasActiveFilter();
    const visibleCount = markers.size;
    $("#count", root).textContent = active ? visibleCount + "/" + ctx.plants.size : String(ctx.plants.size);
    $("#btnList", root).classList.toggle("on", active);
    const bar = $("#filterBar", root);
    if (!active) {
      bar.hidden = true;
      return;
    }
    const parts: string[] = [];
    if (ctx.filter.druh) parts.push(ctx.filter.druh);
    if (ctx.filter.stav) parts.push(STAV_LABEL[ctx.filter.stav]);
    if (ctx.filter.q.trim()) parts.push("„" + ctx.filter.q.trim() + "“");
    $("#filterBarText", root).textContent = "Zobrazeno jen: " + parts.join(" · ") + " (" + visibleCount + " z " + ctx.plants.size + ")";
    bar.hidden = false;
  }

  $("#filterBarClear", root).onclick = () => {
    ctx.filter = { q: "", druh: "", stav: "" };
    ctx.applyFilter();
    toast("Filtr zrušen – zobrazeny všechny rostliny");
  };

  function updatePlaceDistUi() {
    const btn = $("#placeDistToggle", root);
    btn.setAttribute("aria-pressed", String(ctx.showNearestDist));
    if (!ctx.showNearestDist) {
      $("#placeDistText", root).textContent = "";
    }
  }
  $("#placeDistToggle", root).onclick = () => {
    ctx.setShowNearestDist(!ctx.showNearestDist);
  };

  function renderDistOverlay() {
    distLayer.clearLayers();
    if (!ctx.showNearestDist) {
      if (placing) $("#placeDistText", root).textContent = "";
      return;
    }
    if (placing) {
      const cross = $("#cross", root);
      if (cross.hidden) return;
      const ll = map.containerPointToLatLng(crossPoint());
      const info = ctx.nearestTo(ll.lat, ll.lng, placing.excludeId);
      const txtEl = $("#placeDistText", root);
      if (!info) {
        txtEl.textContent = "Žádná další rostlina v zahradě";
        return;
      }
      const numStr = info.plant.cislo != null ? "#" + info.plant.cislo + " " : "";
      txtEl.textContent = info.distText + " od " + numStr + ctx.label(info.plant);
      L.polyline([[ll.lat, ll.lng], [info.plant.lat, info.plant.lng]], {
        color: "#ffd23f",
        weight: 2.5,
        dashArray: "5 5",
        interactive: false,
      })
        .bindTooltip(info.distText, { permanent: true, direction: "center", className: "dist-label" })
        .addTo(distLayer);
      return;
    }

    // When not placing: draw nearest neighbor distance lines for visible plants
    const visible = [...ctx.plants.values()].filter((t) => ctx.matchesFilter(t));
    if (visible.length < 2) return;
    const drawnPairs = new Set<string>();
    for (const a of visible) {
      if (ctx.selected && a.id !== ctx.selected) continue;
      let best: Plant | null = null;
      let bestD = Infinity;
      for (const b of visible) {
        if (b.id === a.id) continue;
        const d = isPhoto
          ? (Math.hypot(a.lat - b.lat, a.lng - b.lng) / 80) * 100
          : L.latLng(a.lat, a.lng).distanceTo([b.lat, b.lng]);
        if (d < bestD) { bestD = d; best = b; }
      }
      if (!best || !Number.isFinite(bestD)) continue;
      const pairKey = a.id < best.id ? a.id + ":" + best.id : best.id + ":" + a.id;
      if (drawnPairs.has(pairKey)) continue;
      drawnPairs.add(pairKey);
      const label = isPhoto
        ? bestD.toFixed(1).replace(".", ",") + " %"
        : bestD < 100
          ? bestD.toFixed(1).replace(".", ",") + " m"
          : Math.round(bestD) + " m";
      L.polyline([[a.lat, a.lng], [best.lat, best.lng]], {
        color: "#ffd23f",
        weight: 2,
        dashArray: "4 5",
        opacity: 0.85,
        interactive: false,
      })
        .bindTooltip(label, { permanent: true, direction: "center", className: "dist-label" })
        .addTo(distLayer);
    }
  }

  async function drawSubgardens() {
    subgardenLayer.clearLayers();
    const allGardens = await store.gardens();
    ctx.subgardens = allGardens.filter((g) => g.parent_id === ctx.garden.id);
    ctx.parentGarden = ctx.garden.parent_id ? (allGardens.find((g) => g.id === ctx.garden.parent_id) ?? null) : null;

    const parentBtn = $("#btnParentGarden", root);
    if (ctx.parentGarden) {
      const pg = ctx.parentGarden;
      parentBtn.hidden = false;
      parentBtn.innerHTML = I.back + "<span>Hlavní zahrada: <b>" + pg.name + "</b></span>";
      parentBtn.onclick = () => { cleanup(); nav.openGarden(pg); };
    } else {
      parentBtn.hidden = true;
    }

    if (isPhoto) return;
    for (const sub of ctx.subgardens) {
      if (sub.parent_lat == null || sub.parent_lng == null) continue;
      const subPlants = (await store.plantsOf(sub.id)).length;
      const icon = L.divIcon({
        className: "",
        html: `<div class="subg-pin" title="Otevřít záhonek ${sub.name}">${I.grid}<span>${sub.name}</span>${subPlants ? `<small>${subPlants}</small>` : ""}</div>`,
        iconSize: [0, 0],
        iconAnchor: [0, 16],
      });
      const m = L.marker([sub.parent_lat, sub.parent_lng], { icon, keyboard: true });
      m.on("click", () => {
        if (placing) return;
        cleanup();
        nav.openGarden(sub);
      });
      m.addTo(subgardenLayer);
    }
  }

  function drawParcels() {
    parcelLayer.clearLayers();
    if (isPhoto) return;
    for (const p of ctx.garden.parcels) {
      L.geoJSON(p.geometry as any, { style: { color: "#ffd23f", weight: 3.5, fillOpacity: 0.06, interactive: false } as any, interactive: false })
        .bindTooltip(p.cislo, { permanent: true, direction: "center", className: "kn-label" }).addTo(parcelLayer);
    }
  }
  function fitGarden() {
    if (isPhoto) {
      map.fitBounds(getPhotoBounds(), { paddingTopLeft: [16, 80], paddingBottomRight: [16, 100] });
    } else if (ctx.garden.parcels.length) {
      map.fitBounds(parcelLayer.getBounds(), { paddingTopLeft: [16, 80], paddingBottomRight: [16, 100], maxZoom: 20 });
    }
  }
  function updateBanner() {
    const b = $("#banner", root);
    const sheetOpen = SHEETS.some((s) => !$("#" + s, root).hidden);
    let text = "";
    if (readOnly) {
      if (!ctx.plants.size) text = "V této sdílené zahradě zatím nejsou žádné rostliny.";
    } else if (isPhoto) {
      if (!ctx.plants.size) text = "Zatím tu není žádná rostlina. Klepni na Přidat rostlinu a umísti křížek na fotku.";
    } else if (!ctx.garden.parcels.length && isOwner) {
      text = "Zahrada zatím nemá parcely. Otevři nabídku a zvol Parcely zahrady.";
    } else if (!ctx.plants.size) {
      text = "Zatím tu není žádná rostlina. Klepni na Přidat rostlinu a umísti křížek na místo výsadby.";
    }
    $("#bannerText", root).textContent = text;
    b.hidden = !text || sheetOpen;
  }

  function crossPoint() {
    const r = $("#cross", root).getBoundingClientRect();
    return rot.screenToContainer(r.left, r.top);
  }
  function panToCross(ll: L.LatLng, z: number) {
    const offset = crossPoint().subtract(map.getSize().divideBy(2));
    map.setView(map.unproject(map.project(ll, z).subtract(offset), z), z, { animate: true });
  }
  function stopPlacing() {
    placing = null;
    rot.setLocked(false);
    $("#cross", root).hidden = true;
    renderDistOverlay();
  }
  map.on("click", (e: L.LeafletMouseEvent) => { if (placing) panToCross(e.latlng, map.getZoom()); });
  map.on("move zoom", () => { if (placing && ctx.showNearestDist) renderDistOverlay(); });
  map.on("dragstart zoomstart", (e: any) => { if (e.originalEvent) userMoved = true; });
  $("#placeCancel", root).onclick = () => { const p = placing; stopPlacing(); ctx.showSheet(null); p?.onCancel?.(); };
  $("#placeOk", root).onclick = () => { const ll = map.containerPointToLatLng(crossPoint()); const p = placing; stopPlacing(); p?.cb(ll); };
  $("#placeGps", root).onclick = () => {
    const f = ctx.gps.last();
    if (!f) return toast("Poloha zatím není k dispozici.");
    panToCross(L.latLng(f.lat, f.lng), Math.max(map.getZoom(), 20));
    if (f.acc > 8) toast("GPS je přesná jen na " + Math.round(f.acc) + " m, dolaď polohu podle snímku.");
  };

  // Compass button: shows current rotation, drag to rotate, tap to reset to North up (0°), and persists rotation per garden
  const btnCompass = $("#btnCompass", root);
  const compassSvg = btnCompass.querySelector("svg") as SVGElement;
  rot.onChange((deg) => {
    compassSvg.style.transform = "rotate(" + deg + "deg)";
    btnCompass.classList.toggle("rotated", Math.round(deg) !== 0);
    try {
      if (Math.abs(deg) < 0.1) localStorage.removeItem("zahrada:rot:" + garden.id);
      else localStorage.setItem("zahrada:rot:" + garden.id, String(Math.round(deg * 10) / 10));
    } catch {}
  });
  let cStartX = 0, cStartY = 0, cStartBearing = 0, cDragged = false;
  btnCompass.addEventListener("pointerdown", (e) => {
    if (rot.isLocked()) return;
    btnCompass.setPointerCapture(e.pointerId);
    cStartX = e.clientX; cStartY = e.clientY; cStartBearing = rot.getBearing(); cDragged = false;
  });
  btnCompass.addEventListener("pointermove", (e) => {
    if (!btnCompass.hasPointerCapture(e.pointerId) || rot.isLocked()) return;
    const dx = e.clientX - cStartX, dy = e.clientY - cStartY;
    if (!cDragged && Math.hypot(dx, dy) > 4) cDragged = true;
    if (cDragged) rot.setBearing(cStartBearing + (dx - dy) * 0.8);
  });
  const endCompassDrag = (e: PointerEvent) => {
    if (!btnCompass.hasPointerCapture(e.pointerId)) return;
    btnCompass.releasePointerCapture(e.pointerId);
    if (!cDragged) {
      if (Math.abs(rot.getBearing()) > 0.5) {
        rot.setBearing(0);
        toast(isPhoto ? "Výchozí otočení fotky" : "Sever nahoře");
      } else {
        toast(isPhoto ? "Fotku otočíš dvěma prsty nebo tažením kompasu." : "Sever je nahoře. Mapu otočíš dvěma prsty nebo tažením kompasu.");
      }
    } else if (Math.abs(rot.getBearing()) < 4) {
      rot.setBearing(0);
    }
  };
  btnCompass.addEventListener("pointerup", endCompassDrag);
  btnCompass.addEventListener("pointercancel", endCompassDrag);

  $("#brand", root).onclick = () => { cleanup(); nav.back(); };
  $("#btnAdd", root).onclick = () => {
    if (readOnly) return;
    ctx.select(null);
    ctx.startPlacing("new", null, (ll) => openForm(ctx, null, ll));
  };
  $("#btnList", root).onclick = () => openList(ctx);
  $("#btnMenu", root).onclick = () => openMenu(ctx);
  $("#btnLayers", root).onclick = () => openLayers(ctx);
  $("#btnFit", root).onclick = fitGarden;
  $("#btnLocate", root).onclick = () => {
    if (isPhoto) return;
    const f = ctx.gps.last();
    if (!f) { ctx.gps.restart(); return toast("Zjišťuji polohu… Povol prosím přístup k poloze."); }
    map.setView([f.lat, f.lng], Math.max(map.getZoom(), 19.5));
  };

  const offState = onSyncState((s) => {
    const dot = $("#syncDot", root), txt = $("#syncText", root);
    if (!dot) return;
    const n = ctx.plants.size, word = n + " " + plural(n, "rostlina", "rostliny", "rostlin");
    if (!s.online) { dot.className = "dot off"; txt.textContent = word + " · offline" + (s.pending ? ", " + s.pending + " čeká" : ""); }
    else if (s.pending) { dot.className = "dot pending"; txt.textContent = word + " · " + s.pending + " čeká na odeslání"; }
    else { dot.className = "dot"; txt.textContent = word + (isDemo() ? " · nic se neukládá" : readOnly ? " · jen pro čtení" : " · uloženo"); }
    if (s.error) txt.textContent += " · " + s.error;
    if (!s.syncing) void ctx.reload();
  });
  const offRemote = onRemoteChange((gid) => { if (gid === garden.id) void ctx.reload(); });
  function cleanup() { offState(); offRemote(); ctx.gps.stop(); setActiveGarden(null); map.remove(); }
  (ctx as any).cleanup = cleanup;

  drawParcels();
  if (isPhoto || garden.parcels.length) fitGarden();
  await ctx.reload();
  setActiveGarden(garden.id);
  void syncSoon();
  if (!isPhoto && (editParcels || !garden.parcels.length) && isOwner) openParcels(ctx);
  return ctx;
}

const blobUrls = new WeakMap<Blob, string>();
export function gardenPhotoSrc(g: Garden) {
  if (g.photo_blob) {
    let u = blobUrls.get(g.photo_blob);
    if (!u) { u = URL.createObjectURL(g.photo_blob); blobUrls.set(g.photo_blob, u); }
    return u;
  }
  return "/api/gardens/" + g.id + "/photo" + (g.updated_at ? "?v=" + encodeURIComponent(g.updated_at) : "");
}

export function photoSrc(p: Photo, size: "thumb" | "full" = "thumb") {
  const blob = size === "thumb" ? (p.thumb ?? p.blob) : (p.blob ?? p.thumb);
  if (blob) {
    let u = blobUrls.get(blob);
    if (!u) { u = URL.createObjectURL(blob); blobUrls.set(blob, u); }
    return u;
  }
  return "/api/photos/" + p.id + (size === "thumb" ? "?size=thumb" : "");
}
export { h };
